import React, { memo } from 'react';
import { PERMISSIONS } from 'constants/permissions';
import { Button, Col, Row, Typography } from 'components';
import { HasAccess } from 'components/common/accessControl';
import {
  SHIPPING_METHOD_MAPPING,
  SHIPPING_SPEED_MAPPING,
} from 'feature/clientTabs/shipping/shippingAddresses/shippingAddress';
import { SHIPPING_MEASUREMENT_RANGE_MAPPING, SHIPPING_RATE_METRICS_MAPPING } from 'staticData/form/client/shipping';
import { DeleteIcon, EditIcon, LeadTimeIcon, OutlineArrowDownIcon, OutlineArrowUpIcon } from 'components/common/icons';
import { useShippingRateCard } from './useShippingRateCard';
import { SupplierSpecialRate } from './../index';
import styles from './../styles.module.scss';

const { Text } = Typography;

interface ISpecialShippingRateCard {
  specialRate: SupplierSpecialRate;
  onHandleEdit: (i: SupplierSpecialRate) => void;
  onHandleDelete: (i: SupplierSpecialRate) => void;
}

export const ShippingRateCard = memo<ISpecialShippingRateCard>(({ specialRate, onHandleEdit, onHandleDelete }) => {
  const { isSeeAllVisible, isAllShown, onShowAllStateChange, priceList } = useShippingRateCard({
    prices: specialRate.prices || [],
  });

  return (
    <Col className={styles.special_rate_card} span={24}>
      <Row justify={'space-between'}>
        <Col>
          <Row align={'middle'} className={'gap-6'}>
            <Text className={'font-weight-600'}>WAREHOUSE IN {specialRate.warehouse?.name}</Text>
          </Row>
        </Col>
        <Col>
          <HasAccess role={PERMISSIONS.suppliers.shipping_rate.delete}>
            <Button type={'link'} size={'small'} icon={<DeleteIcon />} onClick={() => onHandleDelete(specialRate)} />
          </HasAccess>
          <HasAccess role={PERMISSIONS.suppliers.shipping_rate.write}>
            <Button type={'link'} size={'small'} icon={<EditIcon />} onClick={() => onHandleEdit(specialRate)} />
          </HasAccess>
        </Col>
      </Row>
      <Row className={styles.shipping_details} justify={'space-between'} gutter={[0, 14]}>
        <Col>
          {specialRate.shippingMethod && SHIPPING_METHOD_MAPPING[specialRate.shippingMethod] && (
            <Row className={styles.shipping_method_container} align={'middle'}>
              <Col className={styles.shipping_method}>{SHIPPING_METHOD_MAPPING[specialRate.shippingMethod].icon}</Col>
              <Col>
                <Text>{SHIPPING_METHOD_MAPPING[specialRate.shippingMethod].text}</Text>
              </Col>
            </Row>
          )}
        </Col>
        {SHIPPING_SPEED_MAPPING[specialRate.shippingSpeed] && (
          <Col className={styles.shipping_speed}>
            <Text type={'secondary'}>{SHIPPING_SPEED_MAPPING[specialRate.shippingSpeed]}</Text>
          </Col>
        )}
        <Col span={24}>
          <Row justify={'space-between'} align={'middle'}>
            <Col>
              <Row align={'middle'} gutter={[4, 0]}>
                <LeadTimeIcon />
                <Col>
                  <Text type={'secondary'} className={'font-size-16'}>
                    Lead Time
                  </Text>
                </Col>
              </Row>
            </Col>
            <Col>
              <Text type={'secondary'} className={'font-size-16'}>
                {specialRate.leadTimeMin && specialRate.leadTimeMax
                  ? `${specialRate.leadTimeMin}-${specialRate.leadTimeMax} ${specialRate.timeUnit === 'DAY' ? 'days' : 'weeks'}`
                  : specialRate.leadTimeMin
                    ? `Min ${specialRate.leadTimeMin} ${specialRate.timeUnit === 'DAY' ? 'days' : 'weeks'}`
                    : specialRate.leadTimeMax
                      ? `Max ${specialRate.leadTimeMax} ${specialRate.timeUnit === 'DAY' ? 'days' : 'weeks'}`
                      : 'Not Set'}
              </Text>
            </Col>
          </Row>
        </Col>
      </Row>
      {(priceList[0].metrics || priceList[0].measurementRange || priceList[0].price) && (
        <Row>
          <Col span={24}>
            <Text className={styles.price_title}>Price</Text>
          </Col>
          <Col span={24}>
            {priceList.map((price, index: number) => {
              return (
                <Row className={styles.shipping_rate_price} key={index} justify={'space-between'}>
                  <Col>
                    <Text type={'secondary'}>
                      {SHIPPING_RATE_METRICS_MAPPING[price.metrics]}:{' '}
                      {SHIPPING_MEASUREMENT_RANGE_MAPPING[price.measurementRange]}
                    </Text>
                  </Col>
                  <Col>
                    {price.price && (
                      <Text type={'secondary'} className={'font-weight-500'}>
                        {price.price} €
                      </Text>
                    )}
                  </Col>
                </Row>
              );
            })}
          </Col>
          {isSeeAllVisible && (
            <Col span={24}>
              <Row align={'middle'} justify={'end'}>
                <Col>
                  <Button
                    type={'link'}
                    variant={'link'}
                    style={{
                      color: '#308D82',
                      textDecoration: 'underline',
                      padding: 0,
                      margin: 0,
                      fontSize: 14,
                      fontWeight: 400,
                    }}
                    onClick={onShowAllStateChange}
                  >
                    {isAllShown ? 'See Less' : 'See All'}
                    {isAllShown ? (
                      <OutlineArrowUpIcon size={'sm'} color={'#308D82'} />
                    ) : (
                      <OutlineArrowDownIcon size={'sm'} color={'#308D82'} />
                    )}
                  </Button>
                </Col>
              </Row>
            </Col>
          )}
          {specialRate.specialHandling && (
            <>
              <Col span={24}>
                <Text className={styles.special_handling_title}>Special Handling</Text>
              </Col>
              <Col span={24}>
                <Text type={'secondary'}>{specialRate.specialHandling}</Text>
              </Col>
            </>
          )}
        </Row>
      )}
    </Col>
  );
});
